import { append, remove } from 'ramda';
import * as React from 'react';
import { styled } from '@mui/system';
import { OperationContext } from '../config';
import ActionButton from 'Components/ActionButton';
import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import colors from '../../../../config/theme/colors';
import type { IFormContext } from 'Containers/FormContextHandler';
import { withFormContext } from 'Containers/FormContextHandler';
import { createID } from 'Utility/appendIDs';
import Complications from './Complications';
import CyberKnife from './CyberKnife';
import Disconnection from './Disconnection';
import EngelClassification from './EngelClassification';
import IntracranialImaging from './IntracranialImaging';
import Lesionectomy from './Lesionectomy';
import LocalResection from './LocalResection';
import Pad from './Pad';
import Thermocoagulation from './Thermocoagulation';
import { isNil } from 'Utility/ramdaReplacement';
import type { IOperation, TOperation, TTypeOfOperation } from 'neuro-schemas/epilepsySurgicalTreatment';

const StyledDelete = styled('a')({
  fontSize: '1.6rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: colors.primary,
  textTransform: 'uppercase',
  marginRight: '2rem',
});

const Operation = ({ typeOfOperation }: Pick<TOperation, 'typeOfOperation'>): React.JSX.Element => {
  switch (typeOfOperation) {
    case 'localResection':
      return <LocalResection />;
    case 'lesionectomy':
      return <Lesionectomy />;
    case 'disconnection':
      return <Disconnection />;
    case 'cyberKnife':
      return <CyberKnife />;
    case 'thermoCoagulation':
      return <Thermocoagulation />;
    case 'intracranialImaging':
      return <IntracranialImaging />;
    default:
      return <React.Fragment />;
  }
};

const addOperation = (
  onChange: IFormData<IDocumentWithTypeV2<'epilepsySurgicalTreatment'>>['onChange'],
  d: IEpilepsySurgicalTreatment,
): void => {
  onChange?.({
    operations: append({ id: createID() } as TOperation, d.operations ?? []),
  });
};
const deleteOperation = (
  onChange: IFormData<IDocumentWithTypeV2<'epilepsySurgicalTreatment'>>['onChange'],
  d: IEpilepsySurgicalTreatment,
  id: string,
): void => {
  const operation = !isNil(d.operations) && d.operations.length > 0 && d.operations.find(op => op.id === id);
  onChange?.({
    operations: operation ? remove(d.operations!.indexOf(operation), 1, d.operations!) : undefined,
  });
};

const typeOfOperationOnChange =
  (
    onChange: IFormData<IDocumentWithTypeV2<'epilepsySurgicalTreatment'>>['onChange'],
    d: IEpilepsySurgicalTreatment,
    i: number,
  ) =>
  (values: TOnChangeValues): void => {
    if (d.operations?.[i]) {
      const operations = [...d.operations];
      if (!operations[i]) return;
      operations[i] = { ...operations[i], ...values };
      onChange?.({ operations });
    }
  };

const canNewOperationBeAdded = (operations: IEpilepsySurgicalTreatment['operations']): boolean => {
  if (!operations || operations.length === 0) return true;

  const operationsLengthIsLessThanTwo = operations.length < 2;
  const operationIsLocalResesction = operations[0]?.typeOfOperation === 'localResection';
  const operationIsLesionectomy = operations[0]?.typeOfOperation === 'lesionectomy';
  if (operationsLengthIsLessThanTwo && (operationIsLocalResesction || operationIsLesionectomy)) return true;

  return false;
};

const operationTypeOptions = (o: Array<IOperation>, index: number): Array<string> => {
  const firstType = o[0]?.typeOfOperation;
  const secondType = o[1]?.typeOfOperation;

  if (o.length < 2)
    return [
      'localResection',
      'lesionectomy',
      'disconnection',
      'cyberKnife',
      'thermoCoagulation',
      'intracranialImaging',
    ];

  if (index === 0) {
    if (secondType) {
      if (secondType === 'lesionectomy') return ['localResection'];
      return ['lesionectomy'];
    }
    return ['localResection', 'lesionectomy'];
  }

  if (index === 1 && firstType) {
    if (firstType === 'lesionectomy') return ['localResection'];
    return ['lesionectomy'];
  }

  return ['localResection', 'lesionectomy'];
};

const SurgicalTreatmentForm = ({
  formData,
  editing,
  view,
  fm,
}: IFormContext<IDocumentWithTypeV2<'epilepsySurgicalTreatment'>>): React.JSX.Element => {
  const editingDoc = !!editing && !view?.viewing;
  const { document, onChange } = formData;
  const { operations } = document;

  const existingOperationTypes = new Set<TTypeOfOperation>(
    (operations ?? []).map(o => o.typeOfOperation).filter((o): o is TTypeOfOperation => !!o),
  );

  return (
    <React.Fragment>
      <FormSection>
        <FormRow title="surgicalTreatment.date">
          <InputHandler type="PartialDate" editing={editingDoc} name="date" formData={formData} dateDefault="now" />
        </FormRow>
      </FormSection>

      {operations &&
        operations.length > 0 &&
        operations.map((o, i) => (
          <React.Fragment key={o.id}>
            <FormSection header={'surgicalTreatment.operation.operation'}>
              <FormRow title="surgicalTreatment.operation.typeOfOperation.title">
                <Container>
                  <Item style={{ width: '25rem' }}>
                    <InputHandler
                      type="Radio"
                      name="typeOfOperation"
                      editing={editingDoc}
                      formData={{
                        document: { typeOfOperation: document.operations?.[i]?.typeOfOperation },
                        onChange: typeOfOperationOnChange(onChange, document, i),
                      }}
                      options={operationTypeOptions(operations, i)}
                      optionFormatter={(id: string | number): string =>
                        fm(`surgicalTreatment.operation.typeOfOperation.opts.${id}`)
                      }
                      placeholder="surgicalTreatment.operation.typeOfOperation.select"
                      dependentFieldsList={(): string[] => ['locations', 'areas', 'disconnections', 'coagulations']}
                    />
                  </Item>

                  {i === 1 ? (
                    <Item>
                      <StyledDelete onClick={(): void => deleteOperation(onChange, document, o.id)}>
                        {fm('general.delete')}
                      </StyledDelete>
                    </Item>
                  ) : null}
                </Container>
              </FormRow>

              <OperationContext.Provider value={{ operationIndex: i }}>
                <Operation typeOfOperation={o.typeOfOperation} />
              </OperationContext.Provider>
            </FormSection>
          </React.Fragment>
        ))}

      {canNewOperationBeAdded(operations) && (
        <FormSection header={'surgicalTreatment.operation.operation'}>
          <FormRow title="surgicalTreatment.add">
            <ActionButton
              text={'surgicalTreatment.operation.add'}
              onClick={(): void => addOperation(onChange, document)}
              width={20}
              height={5}
              fontSize={18}
            />
          </FormRow>
        </FormSection>
      )}

      {(existingOperationTypes.has('localResection') || existingOperationTypes.has('lesionectomy')) && (
        <FormSection header="surgicalTreatment.pad.header">
          <Pad />
        </FormSection>
      )}

      <FormSection header="surgicalTreatment.complications.title">
        <Complications />
      </FormSection>

      <FormSection header="surgicalTreatment.engelClassification.title">
        <EngelClassification />
      </FormSection>
    </React.Fragment>
  );
};

export default withFormContext(SurgicalTreatmentForm);
